import type { EstimateWarning, EstimatorInput } from "../types/index.js";

/**
 * Structural/input-driven warnings that don't depend on pricing data being
 * present - e.g. flagging unknowns the user should confirm.
 */
export function buildInputDrivenWarnings(input: EstimatorInput): EstimateWarning[] {
  const warnings: EstimateWarning[] = [];

  if (input.designAndPermits.permitLikelyRequired === "unknown") {
    warnings.push({
      code: "permit_requirement_unknown",
      message: "Permit requirements were not confirmed. Check with your local municipality before finalizing budget.",
      severity: "caution"
    });
  }

  if (input.workType.structuralChanges === "unknown") {
    warnings.push({
      code: "structural_scope_unknown",
      message: "Structural work was not confirmed. Unconfirmed structural changes are a common source of budget overruns.",
      severity: "caution"
    });
  }

  if (input.projectType === "other_custom" && !input.customProjectDescription) {
    warnings.push({
      code: "custom_project_undescribed",
      message: "This project was marked as custom/other but no description was provided.",
      severity: "blocking"
    });
  }

  return warnings;
}

export function buildPlaceholderPricingWarning(): EstimateWarning {
  return {
    code: "placeholder_pricing_data",
    message:
      "No pricing data is available at all for this project type. This is a zero-value placeholder result, not a real estimate.",
    severity: "blocking"
  };
}

/**
 * Distinct from `buildPlaceholderPricingWarning`: this fires when the
 * engine *did* compute real numbers from the pricing catalog, but the
 * catalog itself is still sample/unverified data (see
 * packages/pricing-data/data/README.md). The math is real; the dollar
 * figures behind it are not yet.
 */
export function buildSamplePricingDataWarning(): EstimateWarning {
  return {
    code: "sample_pricing_data",
    message:
      "This estimate was computed using an administrator-supplied sample pricing dataset, not verified Canadian pricing data. The methodology is real but the dollar figures are placeholders and must not be shown to end users as a real estimate.",
    severity: "blocking"
  };
}

export function buildManualReviewWarning(reason: string): EstimateWarning {
  return {
    code: "estimate_outside_expected_range",
    message: `This estimate fell outside the expected planning range for this project type and has been flagged for manual review instead of being returned silently. ${reason}`,
    severity: "blocking"
  };
}
